import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type WorkflowStepType,
  type WorkflowStepUser,
  type WorkflowStepInstanceCommentType,
  WorkflowStepInstanceCommentTypes,
  WorkflowStepUserPermissions,
  WorkflowStepUserResponses,
} from "~/api/types";
import { Button } from "~/atoms/button";
import { Label } from "~/atoms/label";
import { Textarea } from "~/atoms/textarea";
import { toast } from "react-toastify";
import { createWorkflowStepInstanceComment } from "~/api/v2/requests";
import {
  usePersistentInput,
  persistentInputTypes,
} from "~/hooks/usePersistentInput";

type Props = {
  currentUserInStep: WorkflowStepUser;
  stepId: number;
  instanceId: number;
  stepType: WorkflowStepType;
  onUserResponse: () => void;
};

export function UserResponseForm({
  currentUserInStep,
  stepId,
  instanceId,
  stepType,
  onUserResponse,
}: Props) {
  const { t } = useTranslation();
  const [comment, setComment, clearComment] = usePersistentInput(
    persistentInputTypes.WORKFLOW_STEP_USER_RESPONSE,
    instanceId,
    "",
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCommentMissingForReject, setIsCommentMissingForReject] =
    useState(false);

  const handleResponse = async (type: WorkflowStepInstanceCommentType) => {
    if (type === WorkflowStepInstanceCommentTypes.rejected && !comment.trim()) {
      setIsCommentMissingForReject(true);
      return;
    }

    try {
      setIsSubmitting(true);
      setIsCommentMissingForReject(false);
      await createWorkflowStepInstanceComment(stepId, type, comment || null);
      clearComment();
      onUserResponse();
    } catch (error) {
      console.error(
        t(($) => $.commons.workflow.failedToUpdateWorkflowStep),
        error,
      );
      toast.error(t(($) => $.commons.workflow.failedToUpdateWorkflowStep));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 rounded-md border border-amber-300 bg-amber-50 p-4 text-amber-800">
      <div className="flex items-center gap-2">
        {t(($) => $.commons.workflow.reviewRespond)}
      </div>
      <div className="text-xs">
        {stepType === "approval"
          ? t(($) => $.commons.workflow.approvalDescription)
          : currentUserInStep.permission === WorkflowStepUserPermissions.write
            ? t(($) => $.commons.workflow.writeDescription)
            : t(($) => $.commons.workflow.readDescription)}
      </div>

      <div>
        <Label htmlFor="response-comment">
          {t(($) => $.commons.workflow.comment)}
        </Label>
        <Textarea
          id="response-comment"
          value={comment}
          onChange={(e) => {
            setComment(e.target.value);
            if (isCommentMissingForReject && e.target.value.trim()) {
              setIsCommentMissingForReject(false);
            }
          }}
          disabled={isSubmitting}
          className="min-h-[50px] text-black"
        />
        {isCommentMissingForReject && (
          <p className="text-sm text-red-600">
            {t(($) => $.validations.workflow.rejectCommentRequired)}
          </p>
        )}
        <div className="flex flex-wrap justify-end gap-2 pt-2">
          <Button
            variant="outline"
            onClick={() =>
              handleResponse(WorkflowStepInstanceCommentTypes.free)
            }
            disabled={isSubmitting || !comment.trim()}
            size="sm"
          >
            {t(($) => $.commons.workflow.respond.comment)}
          </Button>
          {currentUserInStep.responseStatus !==
            WorkflowStepUserResponses.rejected && (
            <Button
              variant="destructive"
              onClick={() =>
                handleResponse(WorkflowStepInstanceCommentTypes.rejected)
              }
              disabled={isSubmitting || isCommentMissingForReject}
              size="sm"
            >
              {t(($) => $.commons.workflow.respond.reject)}
            </Button>
          )}
          {currentUserInStep.responseStatus !==
            WorkflowStepUserResponses.approved && (
            <Button
              onClick={() =>
                handleResponse(WorkflowStepInstanceCommentTypes.approved)
              }
              disabled={isSubmitting}
              size="sm"
            >
              {t(($) => $.commons.workflow.respond.approve)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
